import { Link as RouterLink } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../lib/price';
import { garageCarUrl, carUrl } from '../lib/slugId';
import { recordGarageVisit } from './garageVisits';
import { activeGoals, goalProgress } from './goals/goalProgress';
import { GoalPriorityChip } from './goals/GoalPriorityChip';
import { GoalDeadlineChip } from './goals/GoalDeadlineChip';
import type { Car, GoalPriority } from '../api/types';

/** Progress bars follow the priority badge colour (grey for low). */
const PROGRESS_COLOR: Record<GoalPriority, 'error' | 'warning' | 'inherit'> = {
  high: 'error',
  medium: 'warning',
  low: 'inherit',
};

/** Rows stay scannable: past this many goals the rest is summed up as "+N more". */
const MAX_GOALS_SHOWN = 3;

interface Props {
  car: Car;
  /** Owners open the garage dashboard; followers the public car page. */
  canManage: boolean;
}

/**
 * One car on the garage page: photo, name, and its open goals — most important
 * first — each with its priority, how far along it is and what the rest is
 * estimated to cost.
 */
export function GarageCarRow({ car, canManage }: Props) {
  const { t } = useTranslation();
  const goals = activeGoals(car.goals);
  const shown = goals.slice(0, MAX_GOALS_SHOWN);
  const cover = goals.find((goal) => goal.photos.length > 0)?.photos[0]?.url ?? car.photo_urls[0];

  return (
    <Card variant="outlined">
      <CardActionArea
        component={RouterLink}
        to={canManage ? garageCarUrl(car) : carUrl(car)}
        // Owners are counted when the car dashboard opens; followers here.
        onClick={() => !canManage && recordGarageVisit(car.garage_id)}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }}>
          {cover && (
            // The photo fills the row's height instead of setting it, so a car
            // with no goals is as compact as the others.
            <Box
              sx={{
                position: 'relative',
                flexShrink: 0,
                width: { xs: '100%', sm: 220 },
                minHeight: { xs: 160, sm: 150 },
              }}
            >
              <CardMedia
                component="img"
                image={cover}
                alt={car.display_name}
                sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
              />
            </Box>
          )}
          <CardContent sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="h6">{car.display_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {car.year} {car.make} {car.model}
                </Typography>
              </Box>

              {goals.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  {t('garage.noActiveGoals')}
                </Typography>
              )}

              {shown.map((goal) => {
                const { done, total, percent, remainingCents } = goalProgress(goal);
                return (
                  <Box key={goal.id}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle2" noWrap>
                          {goal.title}
                        </Typography>
                        <GoalPriorityChip priority={goal.priority} />
                        <GoalDeadlineChip goal={goal} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                        {t('garage.goalProgress', { done, total })}
                        {remainingCents > 0 &&
                          ` · ${t('garage.remainingCost', { cost: formatPrice(remainingCents) })}`}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={percent}
                      color={PROGRESS_COLOR[goal.priority]}
                      sx={{ mt: 0.5 }}
                      aria-label={goal.title}
                    />
                  </Box>
                );
              })}

              {goals.length > shown.length && (
                <Typography variant="caption" color="text.secondary">
                  {t('garage.moreGoals', { count: goals.length - shown.length })}
                </Typography>
              )}
            </Stack>
          </CardContent>
        </Stack>
      </CardActionArea>
    </Card>
  );
}
